import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { watermarked } from "../lib/cloudinary";

export default function ProjectDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);

  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [id]);

  if (p === null) return <div className="min-h-screen bg-bone" />;
  if (p === false) return (
    <main className="bg-bone min-h-screen pt-40 px-10">
      <p className="text-muteInk uppercase tracking-[0.24em] text-sm">Project not found.</p>
      <Link to="/work" className="editorial-link mt-4 inline-block">Back to archive</Link>
    </main>
  );

  return (
    <main className="bg-bone" data-testid="project-detail-page">
      <section className="pt-32 md:pt-40 pb-10 md:pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Link to="/work" className="editorial-link text-[11px] uppercase tracking-[0.28em] text-muteInk">← All work</Link>
          <p className="text-[11px] uppercase tracking-[0.3em] text-clay mt-8">{p.category}</p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-[12vw] md:text-[7vw] leading-[0.92] tracking-tight text-ink mt-6"
          >
            {p.title}
          </motion.h1>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-borderWarm pt-8 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-muteInk">Location</p>
              <p className="mt-2 text-ink">{p.location}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-muteInk">Year</p>
              <p className="mt-2 text-ink">{p.year}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-muteInk">Client</p>
              <p className="mt-2 text-ink">{p.client || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-muteInk">Discipline</p>
              <p className="mt-2 text-ink">{p.category}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="clip-frame w-full h-[60vh] md:h-[92vh]">
            <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-4">
            <p className="text-[11px] uppercase tracking-[0.3em] text-muteInk">— Narrative</p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <p className="font-serif text-2xl md:text-4xl leading-[1.25] tracking-tight text-ink">
              {p.description || "A quiet, considered composition."}
            </p>
          </div>
        </div>
      </section>

      {p.images && p.images.length > 0 && (
        <section className="pb-32">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <p className="text-[11px] uppercase tracking-[0.3em] text-muteInk mb-10">— Gallery · {p.images.length} images</p>
            <div className="grid grid-cols-12 gap-4 md:gap-6">
              {p.images.map((src, i) => {
                const layout = i % 5 === 0
                  ? "md:col-span-8 h-[420px] md:h-[680px]"
                  : i % 5 === 1
                  ? "md:col-span-4 h-[300px] md:h-[680px]"
                  : i % 5 === 2 || i % 5 === 3
                  ? "md:col-span-6 h-[320px] md:h-[520px]"
                  : "md:col-span-12 h-[380px] md:h-[620px]";
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                    className={`col-span-12 ${layout} clip-frame hoverable`}
                  >
                    <img src={watermarked(src)} alt="" className="w-full h-full object-cover" />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
